import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { Button } from "@transfr-inc/dashboard-components/forms";

import { useCareerDetails } from "@features/career-details/api/use-career-details";
import { EraseBudgetConfirmationModal } from "@features/shared";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ErrorToast,
  Loader,
  Slide,
  Slideshow,
} from "@features/ui";
import { useAuth } from "@hooks/use-auth";
import { buttonTracking } from "@lib/tracking.helpers";
import { useUserJourney } from "@pages/journey-map/api/use-user-journey";

import {
  BudgetSlideshowFooter,
  SalaryDisplay,
  SavingsDisplay,
} from "./components";
import { slideConfigs } from "./config/slide-configs";
import { useAdjustmentAnimations } from "./hooks/use-adjustment-animations";
import {
  BUDGET_GAME_ACTIONS,
  useBudgetGameController,
} from "./hooks/use-budget-game-controller";
import { BUDGET_GAME_STATES } from "./hooks/use-budget-game-state";
import {
  BudgetOverview,
  PlanCreationFailedView,
  TakeHomePayCalculator,
  TakeHomePayCalculatorLoader,
} from "./views";

import { NUM_INITIAL_BUDGETING_GAME_QUESTIONS } from "src/constants";

import "./index.scss";

export const BudgetingGame = ({ onClose, onBudgetingGameComplete }) => {
  const [
    eraseBudgetConfirmationModalOpen,
    setEraseBudgetConfirmationModalOpen,
  ] = useState(false);

  const { data: userJourney } = useUserJourney();
  const {
    user: { location },
  } = useAuth();

  // ===== CONTROLLER =====

  const {
    // State
    gameState,
    gameContext,
    budgetPlan,
    // Dispatch
    dispatch,
    // Derived values
    currentSlideConfig,
    budgetPlanOptionsByBudgetCategoryCode,
    canAdvance,
    canViewSummary,
    isFirstSlide,
    isLastSlide,
    // Error state
    hasError,
    errorMessage,
    clearError,
    isPlanCreationFailed,
    planCreationError,
    // Loading states
    isLoading: isControllerLoading,
    isSaving,
    isDeletingBudgetPlan,
    isUpdatingBudgetPlan,
    isAddingBudgetPlanOption,
    isAddBudgetPlanOptionSuccess,
    isBudgetPlanFetching,
  } = useBudgetGameController({
    slideConfigs,
    transfrId: userJourney.currentJourneyCareer?.transfrId,
    onBudgetComplete: onBudgetingGameComplete,
  });

  const { currentSlideIndex, totalSlides } = gameContext;

  // ===== ANIMATIONS =====

  const { adjustmentAmount, savingsAdjustmentAmount } = useAdjustmentAnimations(
    {
      budgetPlan,
      isAddBudgetPlanOptionSuccess,
    },
  );

  // ===== CAREER DETAILS =====

  const regionalIncomeTaxRate =
    gameContext.regionalIncomeTaxRate || budgetPlan?.incomeTaxRate;

  // keepPreviousData keeps the full calculator mounted (with old data) while
  // re-fetching for a new region, so we show inline field skeletons rather
  // than replacing the whole slide with TakeHomePayCalculatorLoader.
  const { data: career, isFetching: isFetchingCareerDetails } =
    useCareerDetails({
      transfrId: userJourney.currentJourneyCareer?.transfrId,
      regionAltCode: regionalIncomeTaxRate?.regionAltCode,
      keepPreviousData: true,
    });

  // ===== EVENT HANDLERS (all dispatch to controller) =====

  const handleChoiceChange = useCallback(
    (optionId) => {
      dispatch({
        type: BUDGET_GAME_ACTIONS.SELECT_OPTION,
        payload: { optionId },
      });
    },
    [dispatch],
  );

  const handleStartOver = async () => {
    await dispatch({ type: BUDGET_GAME_ACTIONS.START_OVER });
    setEraseBudgetConfirmationModalOpen(false);
  };

  const handleEdit = useCallback(
    ({ category }) => {
      const slideIndex =
        slideConfigs.findIndex(
          (slide) => slide.budgetCategoryCode === category.code,
        ) + NUM_INITIAL_BUDGETING_GAME_QUESTIONS;
      dispatch({
        type: BUDGET_GAME_ACTIONS.EDIT_CATEGORY,
        payload: { categoryCode: category.code, slideIndex },
      });
    },
    [dispatch],
  );

  const handleViewPathway = useCallback(async () => {
    try {
      await dispatch({ type: BUDGET_GAME_ACTIONS.COMPLETE_BUDGET });
    } catch {
      // Error already surfaced via controller errorMessage; keep the modal
      // open so the user can retry.
    }
  }, [dispatch]);

  const handleRegionalIncomeTaxRateChange = useCallback(
    (taxRate) => {
      dispatch({
        type: BUDGET_GAME_ACTIONS.CHANGE_TAX_RATE,
        payload: { taxRate },
      });
    },
    [dispatch],
  );

  const handleViewSummary = useCallback(() => {
    dispatch({
      type: BUDGET_GAME_ACTIONS.NAVIGATE_TO_SLIDE,
      payload: {
        slideIndex: slideConfigs.length + NUM_INITIAL_BUDGETING_GAME_QUESTIONS,
      },
    });
  }, [dispatch]);

  const handleCalculateTakeHomePay = useCallback(() => {
    dispatch({ type: BUDGET_GAME_ACTIONS.CALCULATE_TAKE_HOME_PAY });
  }, [dispatch]);

  const handleNavigateNext = useCallback(() => {
    dispatch({ type: BUDGET_GAME_ACTIONS.NAVIGATE_NEXT });
  }, [dispatch]);

  const handleNavigatePrevious = useCallback(() => {
    dispatch({ type: BUDGET_GAME_ACTIONS.NAVIGATE_PREVIOUS });
  }, [dispatch]);

  const handleRetryPlanCreation = useCallback(() => {
    dispatch({ type: BUDGET_GAME_ACTIONS.RETRY_PLAN_CREATION });
  }, [dispatch]);

  // ===== DERIVED UI VALUES =====

  const cardSubheader = (() => {
    if (isFirstSlide) {
      return "See net income for...";
    }
    if (isLastSlide) {
      return "Budget summary for...";
    }
    return currentSlideConfig?.title;
  })();

  const budgetTotals = budgetPlan?.budgetTotals ?? { costs: 0, savings: 0 };
  const taxInfo = budgetPlan?.taxInfo ?? { netIncome: 0 };
  const isTakeHomePayCalculated =
    gameContext.isTakeHomePayCalculated ||
    (budgetPlan?.options?.length ?? 0) > 0;

  const isLoading =
    isControllerLoading ||
    gameState.name === BUDGET_GAME_STATES.initializing ||
    gameState.name === BUDGET_GAME_STATES.creatingPlan ||
    (isFetchingCareerDetails && !career?.id);

  // ===== RENDER =====

  return (
    <>
      <Card className="budget-card">
        <CardHeader
          title={
            <>
              <h3 className="subheader">{cardSubheader}</h3>
              {!career?.title && isFirstSlide ? (
                <Skeleton width="14rem" height="2.5rem" />
              ) : (
                <h1>{career?.title}</h1>
              )}
            </>
          }
          toolbar={
            <Button
              onClick={onClose}
              icon={["fa-regular", "xmark"]}
              title="close"
              {...buttonTracking("budget", "close")}
            />
          }
        >
          {!isFirstSlide && (
            <>
              <SalaryDisplay
                salary={taxInfo.netIncome / 12 - budgetTotals.costs}
                adjustmentAmount={adjustmentAmount}
              />
              <SavingsDisplay
                savings={budgetTotals.savings}
                adjustmentAmount={savingsAdjustmentAmount}
              />
            </>
          )}
        </CardHeader>
        <CardBody>
          {isPlanCreationFailed ? (
            <PlanCreationFailedView
              planCreationError={planCreationError}
              onRetry={handleRetryPlanCreation}
              onClose={onClose}
            />
          ) : (
            <Slideshow
              index={currentSlideIndex}
              ariaLabel="budgeting game slides"
              controlsEnabled={false}
            >
              {isLoading ? (
                <TakeHomePayCalculatorLoader key="take-home-pay-calculator" />
              ) : (
                <TakeHomePayCalculator
                  key="take-home-pay-calculator"
                  budgetPlan={budgetPlan}
                  career={career}
                  isLoading={
                    isFetchingCareerDetails ||
                    isBudgetPlanFetching ||
                    isUpdatingBudgetPlan
                  }
                  isTakeHomePayCalculated={isTakeHomePayCalculated}
                  onCalculateTakeHomePay={handleCalculateTakeHomePay}
                  onRegionalIncomeTaxRateChange={
                    handleRegionalIncomeTaxRateChange
                  }
                  regionalIncomeTaxRate={regionalIncomeTaxRate}
                  state={location?.state}
                />
              )}
              {slideConfigs.map(({ id, budgetCategoryCode, Component }) => (
                <Slide key={id}>
                  <Component
                    budgetPlan={budgetPlan}
                    budgetCategoryCode={budgetCategoryCode}
                    isSelecting={
                      isAddingBudgetPlanOption || isBudgetPlanFetching
                    }
                    onChoiceChange={handleChoiceChange}
                    selected={
                      budgetPlanOptionsByBudgetCategoryCode[budgetCategoryCode]
                        ?.budgetCategoryOption
                    }
                  />
                </Slide>
              ))}
              <Slide>
                <BudgetOverview
                  onEdit={handleEdit}
                  slideConfigs={slideConfigs}
                />
              </Slide>
            </Slideshow>
          )}
        </CardBody>
        {!isPlanCreationFailed && (
          <CardFooter className="budget-slideshow-footer">
            <BudgetSlideshowFooter
              slideshow={{
                currentIndex: currentSlideIndex,
                totalSlides,
                next: handleNavigateNext,
                previous: handleNavigatePrevious,
              }}
              nextButtonEnabled={canAdvance}
              onStartOver={() => setEraseBudgetConfirmationModalOpen(true)}
              onViewPathway={handleViewPathway}
              isViewSummaryEnabled={canViewSummary}
              onViewSummary={handleViewSummary}
            />
          </CardFooter>
        )}
      </Card>
      <ErrorToast
        open={hasError}
        message={errorMessage}
        onClose={clearError}
        closable
      />
      <Loader
        show={isSaving}
        text={isDeletingBudgetPlan ? "deleting..." : "saving..."}
      />
      <EraseBudgetConfirmationModal
        career={budgetPlan?.career}
        open={eraseBudgetConfirmationModalOpen}
        onCancel={() => setEraseBudgetConfirmationModalOpen(false)}
        onClose={() => setEraseBudgetConfirmationModalOpen(false)}
        onConfirm={handleStartOver}
      />
    </>
  );
};

BudgetingGame.propTypes = {
  onClose: PropTypes.func,
  onBudgetingGameComplete: PropTypes.func,
};
